import { useEffect, useMemo } from 'react';
import React from 'react';
import { selectShowWarningBanner } from '../../selectors/app.selectors';
import { closeWarningBanner } from '../../actions/app.actions';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import {
	CAPACITY_ITEM,
	palette,
	useIsAdmin,
	useIsFeatureEnabled,
} from '../../utils';
import { useCapacity } from '../../hooks';
import { selectApplicationName, useSelectUserContext } from '../../selectors';
import { RegisteredApplications } from '../../types';
import { Subscription } from '@innovyze/shared-utils';
import { useGlobalization } from '../../contexts';
import AlertMessage from '../../components/AlertMessage';

export const WarningBanner = () => {
	const dispatch = useDispatch();
	const { t } = useGlobalization();

	const warningBannerIsVisible = selectShowWarningBanner();

	const isCapacityPricingEnabledForAsset = !!useIsFeatureEnabled(
		'i360-asset-team-capacity-pricing',
	);
	const isCapacityPricingEnabledForInsight = !!useIsFeatureEnabled(
		'i360i-release-capacity-pricing',
	);

	const isAdmin = useIsAdmin();
	const { entitlements } = useSelectUserContext();
	const applicationName = useSelector(selectApplicationName);
	const isAssetApp =
		applicationName === RegisteredApplications.APP_AM_MAIN ||
		applicationName === RegisteredApplications.APP_AM_ASSET_UPLOAD;

	const isFullAccessUser = useMemo(
		() =>
			entitlements.some(
				entitlement =>
					entitlement === Subscription.I360AFA ||
					entitlement === Subscription.ASSET_FULL_ACCESS,
			),
		[entitlements],
	);

	//For Asset: Only users with full access subscription/entitlement could see the warning banner
	//For Insight: Only users with admin role could see the warning banner
	const canSeeBanner = useMemo(() => {
		if (isAssetApp) {
			return isFullAccessUser;
		} else {
			return isAdmin;
		}
	}, [isAssetApp, isFullAccessUser, isAdmin]);

	const capacity = useCapacity();
	const currentCapacity = capacity.currentCapacity(
		isAssetApp ? CAPACITY_ITEM.I360A_ASSET : CAPACITY_ITEM.I360I_CHANNEL,
	).data;

	useEffect(() => {
		// Only start polling if pricing is enabled AND user should see banner
		if (isAssetApp) {
			if (!isCapacityPricingEnabledForAsset) {
				return;
			}
		} else {
			if (!isCapacityPricingEnabledForInsight) {
				return;
			}
		}
		if (!canSeeBanner || !warningBannerIsVisible) return;

		capacity.queryCapacity(
			isAssetApp
				? CAPACITY_ITEM.I360A_ASSET
				: CAPACITY_ITEM.I360I_CHANNEL,
		);
	}, [
		isCapacityPricingEnabledForAsset,
		isCapacityPricingEnabledForInsight,
		isAssetApp,
		canSeeBanner,
		warningBannerIsVisible,
	]);

	const shouldShowWarningBanner = useMemo(() => {
		if (!currentCapacity) return false; //Capacity API call is not yet complete
		if (!canSeeBanner || !warningBannerIsVisible) return false;
		if (isAssetApp) {
			if (!isCapacityPricingEnabledForAsset) {
				return false;
			}
		} else {
			if (!isCapacityPricingEnabledForInsight) {
				return false;
			}
		}

		return !currentCapacity.unlimited && currentCapacity.available <= 0;
	}, [
		isCapacityPricingEnabledForAsset,
		isCapacityPricingEnabledForInsight,
		currentCapacity,
	]);

	if (!shouldShowWarningBanner) return null;

	const alertBody = (
		<div>
			{t(
				'Your team has used all of your {{capacityUnit}} balance. You can keep working in Info360, but you cannot add new {{actualUnit}}. Free up balance by ',
				{
					capacityUnit: isAssetApp ? t('asset') : t('channel'),
					actualUnit: isAssetApp ? t('assets') : t('sensors'),
				},
			)}
			<RouterLink
				to={
					isAssetApp ? '/data/assets' : '/admin/sensor-configuration'
				}>
				{t('deleting {{actualUnit}}', {
					actualUnit: isAssetApp ? t('assets') : t('sensors'),
				})}
			</RouterLink>
			{t(', or contact sales to purchase a balance add-on.')}
		</div>
	);

	return (
		<AlertMessage
			type="warning"
			bgColor="#f7ebd0"
			sideColor={palette.warning.main}
			iconColor={palette.warning.main}
			showDismissBtn
			onDismiss={() => dispatch(closeWarningBanner())}>
			{alertBody}
		</AlertMessage>
	);
};

export default WarningBanner;
